import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from '../../components/layout/Header';
import CreateBrandModal from './CreateBrandModal';
import EditBrandModal from './EditBrandModal';
import { tenantService } from '../../services/tenantService';
import { Tenant } from '../../types/tenant';
import { useToast } from '../../contexts/ToastContext';

/* -- Stat Card ------------------------------------------------- */
interface StatCardProps {
  label: string;
  value: string;
  trend?: { value: string; isPositive: boolean };
  badge?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, trend, badge }) => (
  <div className="bg-white border border-slate-200 p-5 rounded-xl">
    <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-2">{label}</p>
    <div className="flex items-end gap-3">
      <span className="text-2xl font-black text-slate-900">{value}</span>
      {trend && (
        <span className={`text-xs font-bold mb-1 flex items-center gap-1 ${trend.isPositive ? 'text-emerald-500' : 'text-primary'}`}>
          <span className="material-symbols-outlined text-sm">
            {trend.isPositive ? 'trending_up' : 'trending_down'}
          </span>
          {trend.value}
        </span>
      )}
      {badge && (
        <span className="bg-primary/20 text-primary text-[10px] px-2 py-0.5 rounded font-black mb-1">
          {badge}
        </span>
      )}
    </div>
  </div>
);

/* -- Brand Status Badge ---------------------------------------- */
const StatusBadge: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const config = isActive
    ? { color: 'text-primary', dotColor: 'bg-primary animate-pulse shadow-lg shadow-primary/50', label: 'Active' }
    : { color: 'text-amber-500', dotColor: 'bg-amber-500', label: 'Inactive' };

  return (
    <div className="flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full ${config.dotColor}`} />
      <span className={`text-sm font-bold italic ${config.color}`}>{config.label}</span>
    </div>
  );
};

/* -- Plan Badge ------------------------------------------------ */
const PlanBadge: React.FC<{ planName: string }> = ({ planName }) => {
  const lower = planName.toLowerCase();
  let config = { bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-200' };

  if (lower.includes('pro') || lower.includes('starter')) {
    config = { bg: 'bg-blue-50', text: 'text-blue-500', border: 'border-blue-200' };
  } else if (lower.includes('enterprise')) {
    config = { bg: 'bg-purple-50', text: 'text-purple-500', border: 'border-purple-200' };
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-tighter ${config.bg} ${config.text} border ${config.border}`}>
      {planName}
    </span>
  );
};

/* -- Actions Dropdown ------------------------------------------ */
const ActionsDropdown: React.FC<{
  brand: Tenant;
  onEdit: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
}> = ({ brand, onEdit, onToggleActive, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        className="p-2 text-slate-400 hover:text-slate-700 transition-colors"
      >
        <span className="material-symbols-outlined">more_vert</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 py-1 w-44">
          <button
            onClick={() => { onEdit(); setOpen(false); }}
            className="w-full text-left px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-base">edit</span>
            Edit
          </button>
          <button
            onClick={() => { onToggleActive(); setOpen(false); }}
            className="w-full text-left px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-base">
              {brand.isActive ? 'pause_circle' : 'play_circle'}
            </span>
            {brand.isActive ? 'Deactivate' : 'Activate'}
          </button>
          <div className="border-t border-slate-100 my-1" />
          <button
            onClick={() => { onDelete(); setOpen(false); }}
            className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50 hover:text-red-600 flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-base">delete</span>
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

/* -- Delete Confirmation --------------------------------------- */
const DeleteConfirmation: React.FC<{
  brand: Tenant;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}> = ({ brand, onConfirm, onCancel, isDeleting }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
    <div className="bg-white w-full max-w-md rounded-2xl border border-slate-200 shadow-2xl p-8">
      <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
        <span className="material-symbols-outlined text-red-500 text-2xl">warning</span>
      </div>
      <h3 className="text-lg font-black text-center text-slate-900 mb-2">Delete Brand</h3>
      <p className="text-sm text-slate-500 text-center mb-6">
        Are you sure you want to delete <strong className="text-slate-800">{brand.name}</strong>? This action cannot be undone.
      </p>
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={onCancel}
          disabled={isDeleting}
          className="px-6 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-800 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isDeleting}
          className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-lg text-sm font-bold transition-all disabled:opacity-50 flex items-center gap-2"
        >
          {isDeleting ? 'Deleting...' : 'Delete Brand'}
        </button>
      </div>
    </div>
  </div>
);

/* -- Brands Page ----------------------------------------------- */
const BrandsPage: React.FC = () => {
  const { toast } = useToast();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editBrand, setEditBrand] = useState<Tenant | null>(null);
  const [deleteBrand, setDeleteBrand] = useState<Tenant | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'recent'>('all');
  const [brands, setBrands] = useState<Tenant[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const pageSize = 10;

  const fetchBrands = useCallback(async (page: number = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await tenantService.getAll(page, pageSize);
      if (response.success) {
        setBrands(response.data.items);
        setTotalCount(response.data.totalCount);
        setTotalPages(response.data.totalPages);
        setCurrentPage(page);
      } else {
        setError(response.message || 'Failed to load brands');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load brands');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const handleBrandCreated = () => {
    fetchBrands(1);
  };

  const handleToggleActive = async (brand: Tenant) => {
    try {
      const res = brand.isActive
        ? await tenantService.deactivate(brand.id)
        : await tenantService.activate(brand.id);
      if (res.success) {
        toast.success(`${brand.name} ${brand.isActive ? 'deactivated' : 'activated'}`);
        fetchBrands(currentPage);
      } else {
        toast.error('Failed to update brand status');
      }
    } catch {
      toast.error('Failed to update brand status');
    }
  };

  const handleDelete = async () => {
    if (!deleteBrand) return;
    setIsDeleting(true);
    try {
      const res = await tenantService.delete(deleteBrand.id);
      if (res.success) {
        toast.success(`${deleteBrand.name} deleted`);
        setDeleteBrand(null);
        fetchBrands(currentPage);
      } else {
        toast.error('Failed to delete brand');
      }
    } catch {
      toast.error('Failed to delete brand');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredBrands = brands.filter((brand) => {
    if (activeFilter === 'active') return brand.isActive;
    if (activeFilter === 'recent') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return new Date(brand.createdAt) >= weekAgo;
    }
    return true;
  });

  const activeCount = brands.filter(b => b.isActive).length;

  return (
    <main className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50">
      <Header searchPlaceholder="Search brands, owners, or IDs..." />

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-8">
        {/* Page Title & Actions */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Brand Management</h1>
            <p className="text-slate-500 mt-1">Oversee and manage platform brand partnerships and user access.</p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-5 py-2.5 rounded-lg font-bold text-sm transition-all transform active:scale-95 shadow-lg shadow-primary/20"
          >
            <span className="material-symbols-outlined text-xl">add</span>
            Add New Brand
          </button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard label="Total Brands" value={totalCount.toString()} />
          <StatCard label="Active Brands" value={activeCount.toString()} />
          <StatCard label="Current Page" value={`${currentPage} / ${totalPages || 1}`} />
          <StatCard label="Page Size" value={pageSize.toString()} />
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-500 text-sm font-medium flex items-center gap-3">
            <span className="material-symbols-outlined">error</span>
            {error}
            <button onClick={() => fetchBrands(currentPage)} className="ml-auto text-red-500 hover:text-red-600 underline text-xs">
              Retry
            </button>
          </div>
        )}

        {/* Table Container */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {/* Table Filters */}
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {(['all', 'active', 'recent'] as const).map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-colors ${
                    activeFilter === filter
                      ? 'bg-slate-100 text-slate-700 border border-slate-200'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {filter === 'all' ? 'All Brands' : filter === 'active' ? 'Active Only' : 'Recently Added'}
                </button>
              ))}
            </div>
            <button className="text-slate-400 hover:text-slate-600 p-2">
              <span className="material-symbols-outlined text-xl">filter_list</span>
            </button>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="p-16 flex items-center justify-center">
              <div className="flex items-center gap-3 text-slate-500">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-sm font-medium">Loading brands...</span>
              </div>
            </div>
          ) : filteredBrands.length === 0 ? (
            <div className="p-16 flex flex-col items-center justify-center text-center">
              <span className="material-symbols-outlined text-4xl text-slate-300 mb-3">store</span>
              <p className="text-slate-600 font-bold">No brands found</p>
              <p className="text-slate-400 text-sm mt-1">Create your first brand to get started.</p>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 text-[11px] font-black uppercase tracking-widest">
                      <th className="px-6 py-4">Brand Name</th>
                      <th className="px-6 py-4">Subdomain</th>
                      <th className="px-6 py-4">Current Plan</th>
                      <th className="px-6 py-4">Max Users</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredBrands.map((brand) => (
                      <tr
                        key={brand.id}
                        className="transition-colors hover:bg-primary/5 hover:shadow-[inset_4px_0_0_0_#F97316] group"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center ${brand.isActive ? 'text-primary' : 'text-slate-400'}`}>
                              <span className="material-symbols-outlined text-xl">store</span>
                            </div>
                            <div>
                              <p className={`text-sm font-bold ${!brand.isActive ? 'text-slate-400' : 'text-slate-900'}`}>
                                {brand.name}
                              </p>
                              <p className="text-[10px] text-slate-400 font-mono">ID: {brand.id.slice(0, 8)}...</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-slate-500 font-mono">{brand.subdomain}</span>
                        </td>
                        <td className="px-6 py-4">
                          <PlanBadge planName={brand.planName} />
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-slate-600">{brand.maxUsers}</span>
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge isActive={brand.isActive} />
                        </td>
                        <td className="px-6 py-4 text-right">
                          <ActionsDropdown
                            brand={brand}
                            onEdit={() => setEditBrand(brand)}
                            onToggleActive={() => handleToggleActive(brand)}
                            onDelete={() => setDeleteBrand(brand)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                <p className="text-xs text-slate-500 font-medium tracking-wide">
                  Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)} of {totalCount} brands
                </p>
                <div className="flex items-center gap-2">
                  <button
                    className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 transition-colors disabled:opacity-30"
                    disabled={currentPage <= 1}
                    onClick={() => fetchBrands(currentPage - 1)}
                  >
                    <span className="material-symbols-outlined text-xl">chevron_left</span>
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => fetchBrands(page)}
                      className={`w-8 h-8 rounded-md text-xs font-bold transition-colors ${
                        page === currentPage
                          ? 'bg-primary text-white font-black'
                          : 'hover:bg-slate-100 text-slate-500'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  {totalPages > 5 && (
                    <>
                      <span className="text-slate-400 text-xs font-black">...</span>
                      <button
                        onClick={() => fetchBrands(totalPages)}
                        className={`w-8 h-8 rounded-md text-xs font-bold transition-colors ${
                          totalPages === currentPage
                            ? 'bg-primary text-white font-black'
                            : 'hover:bg-slate-100 text-slate-500'
                        }`}
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                  <button
                    className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 transition-colors disabled:opacity-30"
                    disabled={currentPage >= totalPages}
                    onClick={() => fetchBrands(currentPage + 1)}
                  >
                    <span className="material-symbols-outlined text-xl">chevron_right</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create Brand Modal */}
      <CreateBrandModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleBrandCreated}
      />

      {/* Edit Brand Modal */}
      <EditBrandModal
        isOpen={!!editBrand}
        brand={editBrand}
        onClose={() => setEditBrand(null)}
        onSuccess={() => fetchBrands(currentPage)}
      />

      {/* Delete Confirmation */}
      {deleteBrand && (
        <DeleteConfirmation
          brand={deleteBrand}
          onConfirm={handleDelete}
          onCancel={() => setDeleteBrand(null)}
          isDeleting={isDeleting}
        />
      )}
    </main>
  );
};

export default BrandsPage;
